import React from "react";
import { Star } from "lucide-react";

// ---------------------------------------------------------------------------
// Popular visual treatment — SINGLE SOURCE OF TRUTH
// ---------------------------------------------------------------------------
// The customer-facing Service/Setup configurator renders "Most Popular" tiers,
// kits, add-ons, and standalone items with two distinct visual languages:
//
//   Card treatment (tiers / kits) — floating "Most Popular" pill above the
//   card, thick orange border, subtle orange bg gradient.
//
//   Row treatment (add-ons / standalone items) — inline "Recommended" star+
//   text label above the row name, subtle orange bg tint, orange left-edge bar.
//
// Both are exported as small, dependency-free React nodes so the admin editor
// can drop them into a live preview block next to the "Popular?" checkbox.
// Since PackageBuilder itself imports these same exports, the preview cannot
// drift from what shoppers actually see — that's the point.
// ---------------------------------------------------------------------------

// Card border/bg classes for the "popular" state — applied to the tier/kit
// card outer button on the live site.
export const POPULAR_CARD_CLASSES =
  "border-2 border-[#FF6600] bg-gradient-to-b from-[#FF6600]/[0.06] to-transparent";

// The floating "Most Popular" pill — positioned absolute so it hangs off the
// top edge of a `relative` parent. Optional `testId` for automation.
export const PopularCardBadge = ({ testId }) => (
  <span
    data-testid={testId}
    className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 text-[9px] font-black uppercase tracking-[0.22em] bg-[#FF6600] text-black px-3 py-1 rounded-full inline-flex items-center gap-1 whitespace-nowrap shadow-lg shadow-[#FF6600]/25"
  >
    <Star className="w-3 h-3" fill="currentColor" /> Most Popular
  </span>
);

// The inline "Recommended" marker used on add-on / standalone-item rows —
// small orange caps text with a filled star. Sits above the item name.
export const PopularRowMarker = ({ testId }) => (
  <span
    data-testid={testId}
    className="text-[9px] font-black uppercase tracking-[0.22em] text-[#FF6600] inline-flex items-center gap-1 leading-none"
  >
    <Star className="w-3 h-3" fill="currentColor" strokeWidth={0} /> Recommended
  </span>
);

// Left-edge accent classes for the "popular" state on a row — applied to the
// row's clickable label on the live site.
export const POPULAR_ROW_CLASSES =
  "bg-[#FF6600]/[0.04] border-l-2 border-l-[#FF6600]";

// ---------------------------------------------------------------------------
// Admin-only preview widgets — miniature dummy card/row that renders the
// exact same badge/marker/border treatment so admins can confirm styling
// without leaving the dashboard. Purely visual, non-interactive.
// ---------------------------------------------------------------------------

/**
 * Preview widget for the admin editor. Renders a small dummy tier/kit card
 * or add-on/item row showing what shoppers will see when "Popular?" is on.
 *
 * Props:
 *   kind    "card" (tier/kit) or "row" (add-on / standalone item)
 *   name    optional display name — defaults to a placeholder
 *   testId  optional data-testid for the outer container
 */
export const PopularPreview = ({ kind = "card", name, testId }) => {
  if (kind === "row") {
    return (
      <div
        data-testid={testId || "popular-preview-row"}
        className="rounded-lg border border-[#262626] bg-[#0F0F0F] p-3"
      >
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#7A7A7A] mb-2">
          Live preview — what shoppers see
        </div>
        <div className={`relative flex items-start gap-3 py-2.5 pl-[10px] pr-3 rounded-md ${POPULAR_ROW_CLASSES}`}>
          <span
            aria-hidden
            className="mt-0.5 w-4 h-4 rounded border border-[#3A3A3A] shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <PopularRowMarker />
              <span className="text-sm font-semibold leading-tight text-[#D1D1D1]">
                {name || "Item name"}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default: card preview
  return (
    <div
      data-testid={testId || "popular-preview-card"}
      className="rounded-lg border border-[#262626] bg-[#0F0F0F] p-3"
    >
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#7A7A7A] mb-3">
        Live preview — what shoppers see
      </div>
      <div className="relative flex">
        <PopularCardBadge />
        <div className={`w-full bg-[#1A1A1A] rounded-2xl overflow-hidden flex flex-col p-4 pt-6 ${POPULAR_CARD_CLASSES}`}>
          <div className="w-9 h-9 rounded-xl grid place-items-center shrink-0 bg-[#FF6600] text-black">
            <Star className="w-4 h-4" strokeWidth={2} fill="currentColor" />
          </div>
          <div className="mt-3 min-w-0 flex-1">
            <div className="font-display text-sm font-bold uppercase tracking-wide leading-tight">
              {name || "Package name"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
